import React from "react";
import { RotateCcw, X } from "lucide-react";
import { EmailStatusBadge } from "./EmailMonitor";

const STATUS_OPTIONS = ["new", "reviewed", "contacted", "closed"];

const Field = ({ label, value, testId }) => (
  <div>
    <div className="mb-1 text-[10px] uppercase tracking-[0.22em] text-white/40">{label}</div>
    <div className="break-words text-white/85" data-testid={testId}>{value}</div>
  </div>
);

export const LeadDetailModal = ({ lead, onClose, onStatus, onRetry, retrying, monitoringEnabled }) => {
  if (!lead) return null;
  const canRetry = monitoringEnabled && lead.email_notification_status === "failed";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" data-testid="lead-detail-modal">
      <div className="luxury-glass max-h-[92vh] w-full max-w-2xl overflow-y-auto p-6 md:p-8" role="dialog" aria-modal="true" aria-labelledby="lead-detail-title">
        <div className="mb-7 flex items-start justify-between gap-4">
          <div>
            <h2 id="lead-detail-title" className="font-display text-2xl" data-testid="lead-detail-name">{lead.name}</h2>
            {lead.business_name && <div className="text-sm text-white/50" data-testid="lead-detail-business">{lead.business_name}</div>}
          </div>
          <button type="button" onClick={onClose} className="p-2 text-white/50 transition-colors hover:text-white" aria-label="Cerrar detalle" data-testid="lead-detail-close-button">
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="mb-7 grid gap-4 text-sm sm:grid-cols-2">
          <Field label="Email" value={lead.email} testId="lead-detail-email" />
          <Field label="Teléfono" value={lead.phone} testId="lead-detail-phone" />
          <Field label="Servicio" value={lead.service_type} testId="lead-detail-service" />
          <Field label="Presupuesto" value={lead.budget_range} testId="lead-detail-budget" />
          <Field label="Fecha de inicio" value={lead.preferred_start_date || "—"} testId="lead-detail-start-date" />
          <Field label="Estado" value={lead.status} testId="lead-detail-status" />
          <div className="sm:col-span-2"><Field label="Social" value={lead.social_links || "—"} testId="lead-detail-social" /></div>
          <div className="sm:col-span-2">
            <div className="mb-1 text-[10px] uppercase tracking-[0.22em] text-white/40">Proyecto</div>
            <div className="whitespace-pre-wrap text-white/80" data-testid="lead-detail-project">{lead.project_description}</div>
          </div>
        </div>

        <section className="mb-7 border-y border-white/10 py-5" data-testid="lead-email-delivery-details">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Notificación por correo</div>
            <EmailStatusBadge status={lead.email_notification_status} testId="lead-detail-email-status" />
          </div>
          <div className="grid gap-3 text-xs text-white/55 sm:grid-cols-2">
            <span data-testid="lead-detail-email-attempts">Intentos: {lead.email_notification_attempts || 0}</span>
            <span data-testid="lead-detail-email-last-attempt">
              Último intento: {lead.email_notification_last_attempt_at ? new Date(lead.email_notification_last_attempt_at).toLocaleString() : "—"}
            </span>
          </div>
          {lead.email_notification_error && (
            <p className="mt-4 border-l-2 border-red-400/50 pl-3 text-xs leading-relaxed text-red-200/75" data-testid="lead-detail-email-error">
              {lead.email_notification_error}
            </p>
          )}
          {canRetry && (
            <button
              type="button"
              onClick={() => onRetry(lead.id)}
              disabled={retrying}
              className="mt-5 inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-white/70 transition-colors hover:border-lux/50 hover:text-lux disabled:opacity-40"
              data-testid="lead-email-retry-button"
            >
              <RotateCcw size={13} aria-hidden="true" />
              {retrying ? "Reintentando…" : "Reintentar envío"}
            </button>
          )}
        </section>

        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((status) => (
            <button
              type="button"
              key={status}
              onClick={() => onStatus(lead.id, status)}
              className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${lead.status === status ? "border-white bg-white text-black" : "border-white/15 text-white/60 hover:text-white"}`}
              data-testid={`lead-status-${status}-button`}
            >
              Marcar {status}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};